import { formProps } from "./../../Order/Form";
import {
  Mode,
  ProductType,
  Type,
  OrderPriceType,
  TimeFrame,
  CommissionType,
} from "../../../../utils/Order/Order";
import { mergeDeep, UserType, toDateLocal } from "./../../../../utils/Helper";
import { defaultState } from "./store";

export const orderReducer = (state, action) => {
  switch (action.type) {
    case "CHANGE_FORM":
      return changeForm(state, action);

    case "CHANGE_ORDER_FIELDS":
      return processChangeOrderFields(state, action.fields);

    case "CHANGE_ORDER_ACCOUNT_FIELDS":
      return processChangeOrderAccountFields(state, action.fields);

    case "FETCH_ASSET_INFO":
      return processAssetInfo(state, action.data);

    case "FETCH_ACCOUNT_INFO":
      return processAccountInfo(state, action.items, action.updateOrderValues);

    case "CHANGE_MODE":
      switch (action.mode) {
        case Mode.EDIT:
          return processEdit(state);

        case Mode.PRECHECK:
          return processPrecheck(state);

        case Mode.CONFIRM:
          return processConfirm(state);

        case Mode.EXECUTE:
          return processExecute(state, action.id);

        default:
          return state;
      }

    case "FORCE_UPDATE_VALIDATION":
      return forceUpdateValidation(state, action);

    case "UPDATE_PRECHECK":
      return updatePrecheck(state, action.fields);

    case "CHANGE_BUTTON_PROPS":
      return changeButtonProps(state, action);

    case "EDIT_ACCOUNT":
      return editAccount(state, action);

    case "SET_ATTACHMENT":
      return setAttachment(state, action.payload);

    case "DELETE_ACCOUNT":
      return deleteAccount(state, action);

    case "SELECT_ACCOUNT":
      return selectAccount(state, action);

    case "WIZZ_NEXT_STEP":
      return wizzNextStep(state);

    case "WIZZ_STEP_BACK":
      return wizzStepBack(state);

    default:
      return state;
  }
};

const changeForm = (state, { newForm }) => {
  // from trader can add basket order with empty accounts, when change form retain account setted
  const { OrderHierarchy, OrderIsBasket, OrderHasMultipleAccounts } = state.order;

  return {
    ...state,
    order: {
      ...formProps[newForm].defaults(),
      AssetClassForm: newForm,
      OrderHierarchy,
      OrderIsBasket,
      OrderHasMultipleAccounts,
      OrderSide: state.order.OrderSide || "Buy",
      OrderInitDate: new Date().toDateLocal(),
      OrderType: OrderPriceType.MARKET,
      OrderTimeInForce: TimeFrame.GTC,
      OrderGTD: toDateLocal(new Date()),
      OrderQuantity: state.order.OrderQuantity,
      CurrentAccount: state.order.CurrentAccount
        ? {
            ...state.order.CurrentAccount,
            Precheck: {},
          }
        : {},
      Accounts: state.order.Accounts
        ? state.order.Accounts.map((account) => ({
            ...account,
            Precheck: {},
          }))
        : [],
    },
    entities: {
      settlementCurrencies: null,
    },
    fields: {
      ...state.fields,
      hiddens: {
        ...state.fields.hiddens,
        RequiresSpecialCustody: true,
      },
    },
  };
};

const changeButtonProps = (state, { props }) => {
  return mergeDeep({ ...state }, { buttons: { ...props } });
};

const forceUpdateValidation = (state, { errors }) => {
  return {
    ...state,
    fields: {
      ...state.fields,
      errors,
    },
  };
};

const processChangeOrderFields = (state, fields) => {
  const errors = Object.keys(fields).reduce((o, name) => {
    o[name] = undefined;
    return o;
  }, {});

  const entities = {};

  if (fields.hasOwnProperty("AssetId") && !fields.AssetId) {
    entities.settlementCurrencies = null;
    fields.OrderSettlementCurrencyId = null;
    fields.OrderSettlementCurrencySymbol = null;
  }

  if (state.order.AssetClassForm != ProductType.FOREX) {
    if (fields.hasOwnProperty("OrderSettlementCurrencyId") && fields.OrderSettlementCurrencyId) {
      const filteredCurrencies = state.entities.settlementCurrencies.filter(
        (c) => c.id == fields.OrderSettlementCurrencyId
      );
      if (filteredCurrencies.length > 0) {
        const newCurrency = filteredCurrencies[0];
        fields.OrderSettlementCurrencySymbol = newCurrency.description;
      }
    }
  }

  if (fields.hasOwnProperty("OrderType")) {
    let moo = false;
    if (fields.OrderType == OrderPriceType.MARKETONOPEN) {
      fields.OrderTimeInForce = "OPG";
      moo = true;
    } else if (fields.OrderType == OrderPriceType.MARKETONCLOSE) {
      fields.OrderTimeInForce = TimeFrame.DAY;
      moo = true;
    } else {
      fields.OrderTimeInForce = TimeFrame.GTC;
    }

    if (moo)
      fields.readonly = {
        OrderTimeInForce: true,
      };
    else if (state.ui.mode != Mode.READONLY)
      fields.readonly = {
        OrderTimeInForce: false,
      };
  }

  return {
    ...state,
    order: {
      ...state.order,
      ...fields,
    },
    fields: {
      ...state.fields,
      errors: {
        ...state.fields.errors,
        ...errors,
      },
      readonly: {
        ...state.fields.readonly,
        ...fields.readonly,
      },
    },
    entities: {
      ...state.entities,
      ...entities,
    },
  };
};

const processChangeOrderAccountFields = (state, fields) => {
  const errors = Object.keys(fields).reduce((o, name) => {
    o[name] = undefined;
    return o;
  }, {});

  const { order } = state;
  const { CurrentAccount, Accounts } = order;
  const { index } = CurrentAccount;

  let modFields = fields;

  let orderFields = {};
  if (modFields.hasOwnProperty("Quantity")) {
    // Update order total quantity
    orderFields.OrderQuantity = 0;
    if (index >= 0) {
      if (Accounts) {
        Accounts.forEach((account) => {
          if (account.index !== index) {
            orderFields.OrderQuantity += parseFloat(account.Quantity);
          }
        });
      }
    }
    orderFields.OrderQuantity += modFields.Quantity;
  }

  // Full liquidation option
  if (modFields.hasOwnProperty("IsFullLiquidation")) {
    // This option is valid only on single account orders.
    if (modFields.IsFullLiquidation) {
      orderFields.OrderQuantityIsCash = false;
      modFields.Quantity = CurrentAccount.AccountAssetBalance || 0;
      orderFields.OrderQuantity = modFields.Quantity;
    }
  }
  if (modFields.hasOwnProperty("AccountAssetBalance")) {
    if (CurrentAccount.IsFullLiquidation) {
      // This option is valid only on single account orders.
      modFields.Quantity = modFields.AccountAssetBalance;
      orderFields.OrderQuantity = modFields.Quantity;
    }
  }

  if (modFields.AttachmentFileType) {
    modFields.HasFile = true;
    modFields.HasFileUploadPending = true;
    errors.Instruction = undefined;
  } else {
    modFields.HasFile = false;
    modFields.HasFileUploadPending = false;
    errors.Instruction = undefined;
  }

  if (modFields.hasOwnProperty("AccountUseMarkUP")) {
    if (modFields.AccountUseMarkUP === false) {
      modFields.Markup = 0;
      modFields.FXMarkup = 0;
    }
  }

  return {
    ...state,
    order: {
      ...state.order,
      ...orderFields,
      CurrentAccount: {
        ...state.order.CurrentAccount,
        ...modFields,
      },
    },
    fields: {
      ...state.fields,
      errors: {
        ...state.fields.errors,
        ...errors,
      },
    },
  };
};

const processConfirm_basket = (state) => {
  const { precheck, order } = state;

  let modifyLabel = "Add Another account";
  let precheckFailed = false;

  if (precheck.status == "failed") {
    precheckFailed = true;
    modifyLabel = "Modify";
  }

  return mergeDeep(
    { ...state },
    {
      ui: {
        mode: Mode.PRECHECK,
      },
      order: {
        Accounts: order.Accounts.map((acc) => {
          if (acc.index == order.CurrentAccount.index) {
            return { ...acc, delete: precheckFailed };
          }
          return acc;
        }),
        CurrentAccount: {
          delete: precheckFailed,
        },
      },
      buttons: {
        confirm: {
          visible: precheck.status != "failed",
          label: "Confirm",
        },
        modify: {
          visible: true,
          label: modifyLabel,
          loading: false,
        },
        save: {
          visible: false,
        },
      },
    }
  );
};

const processConfirm_bulk = (state) => {
  const { precheck, order } = state;

  let modifyLabel = "Add another account";
  let precheckFailed = false;

  if (precheck.status == "failed") {
    precheckFailed = true;
    modifyLabel = "Modify";
  }

  return mergeDeep(
    { ...state },
    {
      ui: {
        mode: Mode.PRECHECK,
      },
      order: {
        Accounts: order.Accounts.map((acc) => {
          if (acc.index == order.CurrentAccount.index) {
            return { ...acc, delete: precheckFailed };
          }
          return acc;
        }),
        CurrentAccount: {
          delete: precheckFailed,
        },
      },
      buttons: {
        confirm: {
          visible: precheck.status != "failed",
          label: "Confirm",
        },
        modify: {
          visible: true,
          label: modifyLabel,
        },
        save: {
          visible: false,
        },
      },
    }
  );
};

const processConfirm = (state) => {
  const { ui } = state;

  if (ui.isBasket) {
    return processConfirm_basket(state);
  }

  if (ui.isBulk) {
    return processConfirm_bulk(state);
  }

  let confirmLabel = "Confirm";

  if (
    Object.keys(state.precheck.authorizations).length !== 0 &&
    state.precheck.authorizations.filter((auth) => auth.role == "SELF").length !== 0
  ) {
    confirmLabel = "Authorize and Confirm";
  }

  return mergeDeep(
    { ...state },
    {
      ui: {
        mode: Mode.CONFIRM,
      },
      buttons: {
        confirm: {
          visible: state.precheck.status != "failed",
          label: confirmLabel,
        },
        modify: { visible: true },
      },
      wizzard: {
        nextBtn: {
          ...state.wizzard.nextBtn,
          visible: state.precheck.status != "failed",
        },
      },
    }
  );
};

const processPrecheck = (state) => {
  let errors = validateForm(state);
  const order = {};

  // check if order instruction is written and the file was uploaded
  const { AttachmentFileType, Instruction } = state.order.CurrentAccount;
  if (Instruction === "Written" && (AttachmentFileType == "" || !AttachmentFileType)) {
    if (errors === false) {
      errors = {};
    }
    errors.Instruction = "Attachment required";
  }

  if (errors === false) {
    return mergeDeep(
      { ...state },
      {
        ui: {
          mode: Mode.PRECHECK,
        },
        buttons: {
          save: { visible: false },
          precheck: { visible: false },
          confirm: { visible: false },
        },
        fields: {
          errors,
        },
        order,
      }
    );
  } else {
    return mergeDeep(
      { ...state },
      {
        fields: {
          errors,
        },
      }
    );
  }
};

const processExecute = (state, orderId) => {
  return mergeDeep(
    { ...state },
    {
      ui: {
        mode: Mode.EXECUTE,
      },
      buttons: {
        save: { visible: false },
        precheck: { visible: false },
        confirm: { visible: false },
        modify: { visible: false },
      },
      order: {
        OrderId: orderId,
      },
    }
  );
};

const processAccountInfo = (state, _items, updateOrderValues) => {
  const { ui, order, initFetchs } = state;
  const { CurrentAccount, AssetClassForm } = order;
  const items = _items.reduce((obj, item) => {
    obj[item.key] = item.value;
    return obj;
  }, {});

  const commissionDisabled = items["CommissionEnabled"] == "false" ? "HRO" : undefined;
  const markupHidden = items["MarkupVisible"] == "false" ? true : undefined;
  const FXMarkupAllowed = items["FXMarkupAllowed"] == "false" ? false : true;
  const custodyHidden = items["AsksCustody"] == "false" ? true : undefined;

  let commissionType = CurrentAccount.CommissionType;
  let commissionValue = CurrentAccount.Commission;

  let markupType = CurrentAccount.MarkupType;
  let markupValue = CurrentAccount.Markup;

  let FXMarkupType = CurrentAccount.FXMarkupType;
  let FXMarkupValue = CurrentAccount.FXMarkup;

  let currentInfoDate = CurrentAccount.CurrentInfoDate;

  const accountIsInOrder =
    order.Accounts && order.Accounts.find((acc) => acc.index == CurrentAccount.index) != undefined;
  const isMultiaccount = order.OrderHasMultipleAccounts;

  if (order.OrderId == undefined || initFetchs.accountInfo || (isMultiaccount && !accountIsInOrder)) {
    if (updateOrderValues) {
      markupValue = 0;
      markupType = CommissionType.FaceValueCash;
      FXMarkupValue = 0;
      FXMarkupType = CommissionType.FaceValueCash;
      if (order.OrderId == undefined) {
        currentInfoDate = new Date().toDateLocal();
      }

      if (commissionType == undefined) {
        commissionType = CommissionType.PrincipalPercentage;
      }
      if (commissionValue == undefined) {
        commissionValue = 0;
      }

      if ([ProductType.FOREX].indexOf(AssetClassForm) >= 0) {
        commissionValue = 0;
      } else {
        if (items["CommissionType"]) {
          commissionType = items["CommissionType"];
        }

        if (items["CommissionValue"]) {
          commissionValue = +items["CommissionValue"];
        }
      }
    }
  }

  const nextState = mergeDeep(
    { ...state },
    {
      order: {
        CurrentAccount: {
          Commission: commissionValue,
          CommissionType: commissionType,
          Markup: markupValue,
          MarkupType: markupType,
          FXMarkup: FXMarkupValue,
          FXMarkupType,
          CurrentInfoDate: currentInfoDate,
        },
      },
      precheck: {
        FXMarkupAllowed: FXMarkupAllowed,
      },
      fields: {
        readonly: {
          FXMarkupAllowed: FXMarkupAllowed,
          Commission: ui.mode == Mode.READONLY || commissionDisabled,
          CommissionType: ui.mode == Mode.READONLY || commissionDisabled,
        },
        hiddens: {
          MarkupType: markupHidden,
          Markup: markupHidden,
          RequiresSpecialCustody: state.fields.hiddens.RequiresSpecialCustody || custodyHidden,
        },
      },
      initFetchs: {
        accountInfo: true,
      },
    }
  );

  if (ui.mode == Mode.EDIT_COMMISSION) {
    if (!(commissionDisabled && markupHidden)) {
      nextState.buttons.confirm.visible = true;

      if (nextState.wizzard.enabled) {
        nextState.wizzard.nextBtn.visible = true;
      }
    }
  }

  return nextState;
};

const processAssetInfo = (state, data) => {
  const { order } = state;

  let itemsDict = {};
  data.items.forEach((item) => {
    itemsDict[item.key] = item.value;
  });

  let defaultSettlementCurrency = {};
  let availableSettlementCurrencies = [];

  const settlementCurrencies = data.settlementCurrency.map((c) => {
    const { currencyInfo, multiplies, fx } = c;
    availableSettlementCurrencies.push(c.currencyInfo.currencyId);
    if (currencyInfo.CurrencyIsDefault) {
      defaultSettlementCurrency = {
        id: currencyInfo.currencyId,
        name: currencyInfo.currencyISO,
      };
    }
    return {
      id: currencyInfo.currencyId,
      description: currencyInfo.currencyISO,
      multiplies,
      fx,
    };
  });

  const orderPropsUpd = {};
  const errors = {};

  if (order.AssetClassForm != ProductType.FOREX) {
    if (
      !order.OrderSettlementCurrencyId ||
      !availableSettlementCurrencies.includes(order.OrderSettlementCurrencyId.toString())
    ) {
      orderPropsUpd.OrderSettlementCurrencyId = defaultSettlementCurrency.id;
      orderPropsUpd.OrderSettlementCurrencySymbol = defaultSettlementCurrency.name;
      errors.OrderSettlementCurrencySymbol = undefined;
    }

    order.OrderCurrencySymbol = itemsDict.Currency;
    order.AssetCanBeSelfCustodied = data.custody;
  }

  order.SubAssetClassDecimalPlaces = parseInt(itemsDict.MaxDecimals, 10);

  order.AssetPriceIsDiscounted = false;
  if (itemsDict.AssetPriceIsDiscounted !== undefined) {
    order.AssetPriceIsDiscounted = itemsDict.AssetPriceIsDiscounted.toLowerCase() === "true";
  }

  return mergeDeep(
    { ...state },
    {
      order: {
        ...orderPropsUpd,
      },
      fields: {
        errors: {
          ...errors,
        },
      },
      entities: {
        settlementCurrencies,
      },
    }
  );
};

const processEdit = (state) => {
  const multiAccount = state.ui.isBasket || state.ui.isBulk;

  let merge = mergeDeep(
    { ...state },
    {
      ui: {
        mode: Mode.EDIT,
      },
      buttons: {
        modify: { visible: false },
        precheck: { visible: !multiAccount, label: "Precheck" },
        confirm: { visible: multiAccount },
        save: {
          visible: state.ui.userRole == UserType.Advisor && !multiAccount && !state.precheck.validations,
        },
      },
      precheck: {
        authorizations: [],
        validations: null,
        histories: [],
        status: undefined,
      },
    }
  );

  if (multiAccount) {
    let editAccount = { AccountId: null };
    if (state.precheck.status == "failed") {
      editAccount = state.order.CurrentAccount;
      editAccount.delete = true;
    }
    merge.order.CurrentAccount = editAccount;
  }

  return merge;
};

const validateForm = (state) => {
  const { ui, order, fields } = state;
  const { hiddens, readonly } = fields;
  const { validation } = formProps[order.AssetClassForm];

  const errors = {};

  Object.entries(validation(order, ui.userRole)).forEach(([name, value]) => {
    if (!hiddens[name] && !readonly[name]) {
      if (value.empty) {
        // Verify if value should be controlled for emptyness

        if (value.accountLevel) {
          // Value should be controlled in order accounts.
          if (order.OrderHasMultipleAccounts) {
            if (order.Accounts && order.Accounts.length > 0) {
              // View Accounts list
              order.Accounts.forEach((account) => {
                if (account.AccountId != 0) {
                  if (account[name] == undefined || account[name] === "") {
                    errors[name] = "Required field";
                  } else if (value.autosuggestId && account[value.autosuggestId] == undefined) {
                    errors[name] = "Must select a value from list";
                  }
                }
              });
            }
          } else {
            // View CurrentAccount
            if (order.CurrentAccount[name] == undefined || order.CurrentAccount[name] === "") {
              errors[name] = "Required field";
            } else if (value.autosuggestId && order.CurrentAccount[value.autosuggestId] == undefined) {
              errors[name] = "Must select a value from list";
            }
          }
        } else {
          if (order[name] == undefined || order[name] === "") {
            errors[name] = "Required field";
          } else if (value.autosuggestId && order[value.autosuggestId] == undefined) {
            errors[name] = "Must select a value from list";
          }
        }
      }
    }
  });

  if (ui.userRole != UserType.Trader && ui.isBulk) {
    if (order.Accounts == undefined || order.Accounts.length == 0) {
      errors["Accounts"] = "Require an account";
    }
  }

  if (ui.mode == Mode.REALLOCATE) {
    if (order.CurrentAccount.AccountId == order.CurrentAccountId) {
      errors["AccountName"] = "Must be different from the order being reallocated";
    }
  }

  if (ui.type == Type.Cross) {
    // if second order form, check quantity over or equal than first order
    if (state.wizzard.enabled && state.wizzard.activeStep == 2) {
      if (!errors.OrderQuantity) {
        if (+state.wizzard.steps[0].context.order.OrderQuantity > +order.OrderQuantity) {
          errors[
            "OrderQuantity"
          ] = `Must be equal or grather than ${state.wizzard.steps[0].context.order.OrderQuantity}`;
        }
      }
    }
  }

  // has Errors only add errors to state
  if (Object.keys(errors).length !== 0) {
    return errors;
  }

  return false;
};

const updatePrecheck = (state, props) => {
  const newProps = {};

  if (props.order) {
    const { order } = props;
    const { OrderChainOffsetCash, OrderChainOffsetCurrencyId, AssetId } = order;

    newProps.order = {
      OrderChainOffsetCash,
      OrderChainOffsetCurrencyId,
      CurrentAccount: {
        Precheck: order.CurrentAccount.Precheck,
      },
      AssetId,
    };

    newProps.precheck = order.CurrentAccount.Precheck;
  } else {
    newProps.precheck = {
      ...props,
    };
  }
  return mergeDeep({ ...state }, { ...newProps });
};

const editAccount = (state, { props }) => {
  const { Accounts } = state.order;
  const { index } = props.account;
  const others = {};
  const errors = {};
  const order = {};

  const { account } = props;

  if (props.type == Type.Basket || props.type == Type.Bulk) {
    others.ui = {
      mode: Mode.PRECHECK,
    };

    others.buttons = {
      save: { visible: false },
      precheck: { visible: false },
      confirm: { visible: false },
    };

    //update errors
    errors.Accounts = undefined;
    errors.OrderQuantity = undefined;

    others.fields = { errors };

    account.pendingServerUpdate = true;
  }

  let newState = mergeDeep(
    { ...state },
    {
      ...others,
    }
  );

  if (props.type == Type.Basket || props.type == Type.Bulk) {
    newState.order.Accounts = Accounts || [];
    if (index >= 0) {
      newState.order.Accounts = newState.order.Accounts.filter((acc) => acc.index !== index);
    }
    newState.order.Accounts.push(account);
  }

  newState.order.OrderQuantity = newState.order.Accounts.reduce((sum, a) => sum + parseFloat(a.Quantity), 0);

  newState.order.CurrentAccount = account;

  return newState;
};

const deleteAccount = (state, { accountIndex }) => {
  const { Accounts } = state.order;

  let accountId = -1;
  if (accountIndex >= 0 && Accounts) {
    Accounts.find((acc) => {
      if (acc.index === accountIndex) {
        accountId = acc.AccountId;
        return true;
      }
      return false;
    });
  }

  const accountsUpd = Accounts.filter((acc) => acc.index !== accountIndex);
  const OrderQuantity = accountsUpd.reduce((sum, a) => sum + parseFloat(a.Quantity), 0);

  return {
    ...state,
    order: {
      ...state.order,
      Accounts: accountsUpd,
      OrderQuantity,
      CurrentAccount: {},
    },
    attachments: {
      ...state.attachments,
      [accountId]: undefined,
    },
  };
};

const selectAccount = (state, { props }) => {
  const { Accounts } = state.order;
  const { accountIndex } = props;

  const nextIndex = Accounts ? Accounts.length : 0;

  let account;

  if (accountIndex >= 0 && Accounts) {
    account = Accounts.find((acc) => acc.index === accountIndex);
  }

  if (account === undefined) {
    account = {
      index: nextIndex,
      CommissionType: CommissionType.PrincipalPercentage,
      MarkupType: CommissionType.FaceValueCash,
      FXMarkupType: CommissionType.FaceValueCash,
      CurrentInfoDate: toDateLocal(new Date()),
    };
  }

  return {
    ...state,
    order: {
      ...state.order,
      CurrentAccount: account,
    },
  };
};

const setAttachment = (state, { file, id }) => {
  return {
    ...state,
    attachments: {
      ...state.attachments,
      [id]: file,
    },
    order: {
      ...state.order,
      AttachmentFileUploadPending: true,
    },
    buttons: {
      ...state.buttons,
      save: {
        ...state.buttons.confirm,
        label: "Save",
        visible: state.ui.userRole === UserType.MiddleOffice,
      },
    },
  };
};

const wizzStepBack = (state) => {
  const { wizzard, ...actualState } = state;
  const { activeStep } = state.wizzard;

  return {
    ...wizzard.steps[activeStep - 1].context,

    wizzard: {
      ...wizzard,
      activeStep: activeStep - 1,
      nextBtn: {
        visible: true,
        label: wizzard.steps[activeStep - 1].nextLabel,
      },
      steps: {
        ...wizzard.steps,
        [activeStep]: {
          ...wizzard.steps[activeStep],
          context: actualState,
        },
      },
    },
  };
};

const wizzNextStep = (state) => {
  const { wizzard, ...oldState } = state;
  const nextBtn = {};

  let nextState;

  if (state.wizzard.activeStep == 0) {
    // perform precheck first order
    nextState = processPrecheck(state);
    nextBtn.visible = false;

    // precheck failed
    if (nextState.ui.mode != Mode.PRECHECK) {
      return nextState;
    }
  } else if (state.wizzard.activeStep == 1) {
    // perform prepare form for second order
    const { buttons, fields, order, initFetchs, precheck } = defaultState();

    let copyProps = {};
    let readonly = { ...fields.readonly };

    if (state.ui.type == Type.Chained) {
      const { precheck } = state;
      copyProps = copyChainedOrderPropsNextStep(wizzard.steps[0].context.order, precheck);
      readonly.AccountName = true;
    }

    if (state.ui.type == Type.Cross) {
      copyProps = copyCrossOrderPropsNextStep(wizzard.steps[0].context.order);
    }

    nextBtn.visible = true;

    if (wizzard.steps[2].context != null) {
      nextState = mergeFirstStepChangesToNextStep(state);
    } else {
      nextState = {
        ui: {
          ...state.ui,
          mode: Mode.EDIT,
        },
        buttons,
        fields: {
          ...fields,
          readonly,
        },
        order: {
          ...order,
          ...formProps[state.order.AssetClassForm].defaults(Type.Default),
          ...copyProps,
        },
        entities: {
          ...wizzard.steps[0].context.entities,
        },
        initFetchs,
        precheck,
      };
    }

    nextState.wizzard = { ...state.wizzard };

    if (nextState.order.OrderHierarchy == Type.Chained) {
      const readOnlyAssetForm = readonly.AssetClassForm || state.order.OrderIsSwitch;
      nextState.fields.readonly.OrderSide = true;
      nextState.fields.readonly.AssetClassForm = readOnlyAssetForm;
      nextState.fields.readonly.OrderIsSwitch = true;

      nextState.order.OrderSide = "Buy";
      nextState.order.OrderIsSwitch = state.order.OrderIsSwitch;
      nextState.order.OrderChainOffsetCash = state.order.OrderChainOffsetCash;
      nextState.order.OrderChainOffsetCurrencyId = state.order.OrderChainOffsetCurrencyId;
      nextState.entities = {
        settlementCurrencies: null,
      };
    }

    if (nextState.order.OrderHierarchy == Type.Cross) {
      nextState.fields.readonly.OrderType = true;
      nextState.fields.readonly.OrderSide = true;
      nextState.fields.readonly.AssetTicker = true;
      nextState.fields.readonly.AssetClassForm = true;
      nextState.order.OrderSide = state.order.OrderSide == "Buy" ? "Sell" : "Buy";
    }
  } else if (state.wizzard.activeStep == 2) {
    // perform precheck second order
    nextState = processPrecheck(state);

    // precheck failed
    if (nextState.ui.mode != Mode.PRECHECK) {
      return nextState;
    }
  }

  const nextStep = nextState.wizzard.activeStep + 1;

  nextState.wizzard = {
    ...wizzard,
    activeStep: nextStep,
    nextBtn: {
      label: wizzard.steps[nextStep].nextLabel,
      ...nextBtn,
    },

    steps: {
      ...wizzard.steps,
      [wizzard.activeStep]: {
        ...wizzard.steps[wizzard.activeStep],
        context: oldState,
      },
    },
  };

  nextState.ui.title = getTitle({ ui: state.ui, wizzard: nextState.wizzard });

  return nextState;
};

const getTitle = ({ ui, wizzard }) => {
  let typeName = "";
  if (ui.isBulk) {
    typeName = " Bulk";
  } else if (ui.isBasket) {
    typeName = " Basket";
  }

  if (ui.mode == Mode.REALLOCATE) {
    return `Reallocate Order`;
  }

  if (ui.type == Type.Chained || ui.type == Type.Cross) {
    if (ui.mode == Mode.EDIT_COMMISSION) {
      return `Offer (from ${ui.type})`;
    } else if (wizzard.enabled && ui.mode != Mode.READONLY) {
      return wizzard.steps[wizzard.activeStep].title;
    } else {
      return `View Order (from ${ui.type})`;
    }
  }

  if (wizzard.enabled && ui.mode != Mode.READONLY) {
    return wizzard.steps[wizzard.activeStep].title;
  } else if (ui.mode != Mode.READONLY) {
    return `Edit${typeName} Offer`;
  } else {
    return `View${typeName} Offer`;
  }
};

function copyChainedOrderPropsNextStep(order, precheck) {
  const { OrderHierarchy, OrderIsBasket, OrderHasMultipleAccounts, OrderIsSwitch } = order;

  const { AccountId, AccountNumber, AccountName, HolderId, HolderName, Instruction, IsFullLiquidation } =
    order.CurrentAccount;

  const switchProps = {};
  const switchAccountProps = {};

  // copy first order IsSwitch
  switchProps.OrderIsSwitch = OrderIsSwitch;

  if (OrderIsSwitch) {
    // order level props
    switchProps.OrderQuantity = precheck.Principal;
    switchProps.AssetClassForm = order.AssetClassForm;
    switchProps.OrderQuantityIsCash = true;
    // account props
    switchAccountProps.Quantity = precheck.Principal;
    switchAccountProps.IsFullLiquidation = false;
  }

  return {
    OrderHierarchy,
    OrderIsBasket,
    OrderHasMultipleAccounts,
    ...switchProps,
    CurrentAccount: {
      AccountId,
      AccountNumber,
      AccountName,
      HolderId,
      HolderName,
      Instruction,
      ...switchAccountProps,
    },
  };
}

function copyCrossOrderPropsNextStep(order) {
  const {
    OrderHierarchy,
    OrderIsBasket,
    OrderHasMultipleAccounts,
    AssetClassForm,
    AssetDescription,
    AssetId,
    AssetTicker,
    AssetSecurityProvider,
    OrderSettlementCurrencyId,
    OrderSettlementCurrencySymbol,
  } = order;
  return {
    OrderHierarchy,
    OrderIsBasket,
    OrderHasMultipleAccounts,
    AssetClassForm,
    AssetDescription,
    AssetId,
    AssetTicker,
    AssetSecurityProvider,
    OrderSettlementCurrencyId,
    OrderSettlementCurrencySymbol,
  };
}

function mergeFirstStepChangesToNextStep(state) {
  const { wizzard, precheck } = state;
  const step1_order = wizzard.steps[0].context.order;
  const step2 = { ...wizzard.steps[2].context };

  if (step1_order.OrderIsSwitch) {
    // copy fields again
    const copyProps = copyChainedOrderPropsNextStep(step1_order, precheck);
    return {
      ...step2,
      order: {
        ...step2.order,
        ...copyProps,
      },
    };
  }

  return step2;
}

export { validateForm, getTitle };
